import { useMemo, useState } from 'react';

import { useQueries } from '@tanstack/react-query';

import type { SymbolPreviewItem } from '@/features/_shared/SymbolsPreview';
import { catalogQueryOptions } from '@/features/ai-chat/api/chat.options';
import { extractGxCodesFromMessage } from '@/features/ai-chat/lib/gxCodes';

import type { UIMessage } from '@tanstack/ai-client';

const MAX_PREVIEW_ITEMS = 5;

export function useSymbolsPreview(msg: UIMessage) {
  const [isExpanded, setIsExpanded] = useState(false);

  const gxCodes = useMemo(() => extractGxCodesFromMessage(msg), [msg]);
  const trimmedGxCodes = useMemo(
    () => (isExpanded ? gxCodes : gxCodes.slice(0, MAX_PREVIEW_ITEMS)),
    [gxCodes, isExpanded]
  );
  const queries = useMemo(
    () => trimmedGxCodes.map(code => catalogQueryOptions(code)),
    [trimmedGxCodes]
  );

  const results = useQueries({
    combine: rs => ({
      data: rs.map(r => r.data as SymbolPreviewItem),
      pending: rs.some(r => r.isPending),
      success: rs.every(r => r.isSuccess)
    }),
    queries
  });

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  return {
    gxCodes,
    trimmedGxCodes,
    results,
    isExpanded,
    toggleExpanded,
    hasMore: gxCodes.length > MAX_PREVIEW_ITEMS
  } as const;
}
